import React, { useState, useRef } from "react";
import { Label } from "react-aria-components";
import { DropdownCondition } from "../../queries/getCrmRequests";
import { Dropdown } from "semantic-ui-react";
import { MultiStringInput } from "./StringFilter";
import { IBaseControlProps, nestedErrorMessageSelector } from "./Utils";
import { Label as SemanticLabel } from "semantic-ui-react";
import { Controller } from "react-hook-form";

const DropdownOperatorOptions: {
  key: DropdownCondition;
  text: string;
  value: DropdownCondition;
}[] = [
  { key: "Any", text: "Is", value: "Any" },
  { key: "NotAny", text: "Is Not", value: "NotAny" },
  { key: "Empty", text: "Is Empty", value: "Empty" },
  { key: "NotEmpty", text: "Is Not Empty", value: "NotEmpty" },
  { key: "Contains", text: "Contains", value: "Contains" },
  { key: "NotContains", text: "Does Not Contain", value: "NotContains" },
  { key: "StartsWith", text: "Starts With", value: "StartsWith" },
  { key: "NotStartsWith", text: "Does Not Start With", value: "NotStartsWith" },
];
interface IDropdownFilterProps {
  label: string;
  defaultValues?: (string | number)[];
  options: {
    key: string;
    value: string;
    text: string | JSX.Element;
    header?: boolean;
    disabled?: boolean;
    className?: string;
  }[];
  defaultOperator?: DropdownCondition;
  className?: string;
  useEmptyOperator?: boolean;
  onChange?: (values: (string | number)[]) => void;
  onOperatorChange?: (operator: DropdownCondition, values: (string | number)[]) => void;
  onSearchChange?: (searchQuery: string) => void;
  isLoading?: boolean;
  stringFieldWhenNoOptions?: boolean;
}

const isDropdownOperatorCheck = (
  operator: DropdownCondition,
  options: {
    key: string;
    value: string;
    text: string | JSX.Element;
    header?: boolean;
    disabled?: boolean;
    className?: string;
  }[],
  stringFieldWhenNoOptions: boolean,
) => {
  return (!!options?.length || !stringFieldWhenNoOptions) && (operator === "Any" || operator === "NotAny");
};

export const DropdownFilter = ({
  label,
  defaultValues = [],
  options,
  defaultOperator = "Any",
  className,
  useEmptyOperator = true,
  onChange,
  onOperatorChange,
  onSearchChange,
  isLoading = false,
  stringFieldWhenNoOptions = true,
}: IDropdownFilterProps) => {
  const isDefaultOperatorDropdown = isDropdownOperatorCheck(defaultOperator, options, stringFieldWhenNoOptions);
  const [dropdownValues, setDropdownValues] = useState<(string | number)[]>(
    isDefaultOperatorDropdown ? defaultValues.map(String) : [],
  );
  const [stringValues, setStringValues] = useState<string[]>(
    !isDefaultOperatorDropdown ? defaultValues.map(String) : [],
  );
  const [operator, setOperator] = useState<DropdownCondition>(defaultOperator);
  const shouldRenderInput = operator !== "Empty" && operator !== "NotEmpty";

  const isDropdownOperator = isDropdownOperatorCheck(operator, options, stringFieldWhenNoOptions);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={className}>
      <div className='tw-flex tw-w-full tw-items-center'>
        <Label className='tw-font-bold'>{label}</Label>
        <Dropdown
          id='String-range-operator-dropdown'
          className='tw-ml-4 tw-font-bold tw-text-blue'
          options={
            useEmptyOperator
              ? DropdownOperatorOptions
              : DropdownOperatorOptions.filter((option) => option.value !== "Empty" && option.value !== "NotEmpty")
          }
          defaultValue={operator}
          onChange={(_, { value }) => {
            setOperator(value as DropdownCondition);
            if (onOperatorChange) {
              if (value === "Empty" || value === "NotEmpty") {
                onOperatorChange(value as DropdownCondition, []);
                return;
              }
            }

            const isNewOperatorDropdown = isDropdownOperatorCheck(
              value as DropdownCondition,
              options,
              stringFieldWhenNoOptions,
            );
            if (!isNewOperatorDropdown) {
              const currentValue = inputRef.current?.value;
              const valuesToUse = currentValue ? [...stringValues, currentValue.trim()] : stringValues;
              onOperatorChange?.(value as DropdownCondition, valuesToUse);
              return;
            }
            //make sure to save the current string value from the input
            const currentValue = inputRef.current?.value;
            const valuesToUse = currentValue ? [...stringValues, currentValue.trim()] : stringValues;
            setStringValues(valuesToUse);
            onOperatorChange?.(value as DropdownCondition, dropdownValues);
          }}
        />
      </div>
      {shouldRenderInput && (
        <div className='tw-mt-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-0 tw-shadow-none'>
          {isDropdownOperator && (
            <Dropdown
              fluid
              multiple
              search
              selection
              options={options}
              loading={isLoading}
              defaultValue={defaultValues}
              value={dropdownValues}
              onSearchChange={(_, { searchQuery }) => {
                onSearchChange?.(searchQuery);
              }}
              onChange={(_, { value }) => {
                const newValues = (value as (string | number)[]).filter((v) => v !== null && v !== undefined);
                setDropdownValues(newValues);
                onChange?.(newValues);
              }}
            />
          )}
          {!isDropdownOperator && (
            <MultiStringInput
              inputRef={inputRef}
              values={stringValues}
              setValues={setStringValues}
              onChange={onChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface IControlledDropdownFilterProps extends IBaseControlProps, IDropdownFilterProps {}

export const ControlledDropdownFilter = ({ control, name, errors, ...props }: IControlledDropdownFilterProps) => {
  const errorMessage = nestedErrorMessageSelector(errors)(name);
  return (
    <Controller
      control={control}
      name={name}
      errors={errors}
      defaultValue={{
        jsonValue: props.defaultValues || [],
        dropdownCondition: props.defaultOperator || "Any",
      }}
      render={({ onChange, value: formValue }) => (
        <>
          <DropdownFilter
            {...props}
            defaultValues={formValue?.jsonValue || props.defaultValues}
            defaultOperator={formValue?.dropdownCondition || props.defaultOperator}
            onChange={(values) => {
              onChange({
                ...formValue,
                jsonValue: values,
              });
              if (props.onChange) {
                props.onChange(values);
              }
            }}
            onOperatorChange={(operator, values) => {
              onChange({
                ...formValue,
                dropdownCondition: operator,
                jsonValue: values,
              });
              if (props.onOperatorChange) {
                props.onOperatorChange(operator, values);
              }
            }}
          />
          {!!errorMessage && (
            <SemanticLabel className='tw-ml-0' basic prompt pointing='above'>
              {errorMessage}
            </SemanticLabel>
          )}
        </>
      )}
    />
  );
};
